import { gql, useMutation } from '@apollo/client';
import { EventHandler, useCallback, useState } from 'react';
import useNotify from './useNotify';
import { maybeNumber } from '@ecosconnect/core/src/helpers/number';

export const SAVE_FORM_VALUES = gql`
  mutation SetJobFormValues($jobId: ID!, $values: JSON!) {
    setJobFormValues(jobId: $jobId, values: $values) {
      id
    }
  }
`;

export type SetFormValue<FormDataType> = <Key extends keyof FormDataType>(
  field: Key,
  value:
    | FormDataType[Key]
    | string
    | null
    | ((oldValue: FormDataType[Key]) => FormDataType[Key])
) => void;

export type SetFormValues<FormDataType> = <Key extends keyof FormDataType>(
  values:
    | Partial<FormDataType>
    | Record<Key, string | number | undefined | null>
    | ((oldValues: Partial<FormDataType>) => Partial<FormDataType>)
) => void;

export const useJobFormData = <FormDataType>(initialJobId?: string) => {
  const { showError } = useNotify();
  const [jobId, setJobId] = useState<string | undefined>(initialJobId);
  const [formValues, setFormValuesLocal] = useState<
    Partial<FormDataType> | undefined
  >();

  const [saveFormValues] = useMutation<
    {
      setJobFormValues: null;
    },
    {
      jobId: string;
      values: Partial<FormDataType>;
    }
  >(SAVE_FORM_VALUES, {
    onCompleted: (data, clientOptions) => {
      // console.log(
      //   `Saved form field: ${clientOptions?.variables?.key}: ${JSON.stringify(clientOptions?.variables?.value)}`
      // );
    },
    onError: (error, clientOptions) => {
      showError(
        `Error saving form values: ${clientOptions?.variables?.values}`,
        error
      );
    },
  });

  const setFormValues = useCallback<SetFormValues<FormDataType>>(
    (values) => {
      if (!formValues) {
        return;
      }

      const newValues =
        typeof values === 'function'
          ? values(formValues)
          : (values as Partial<FormDataType>);

      const keys = Object.keys(values) as (keyof FormDataType)[];
      let isChanged = false;
      keys.forEach((key: keyof FormDataType) => {
        const oldValue = formValues[key];
        let value = newValues[key];
        if (typeof value === 'string') {
          if (value.length > 255) {
            value = value.substring(0, 255) as any;
          }
          value = maybeNumber(value as string) as any;
        }
        if (JSON.stringify(oldValue) !== JSON.stringify(value)) {
          isChanged = true;
        }
        (newValues as any)[key] = value;
      });

      if (!isChanged) {
        return;
      }

      setFormValuesLocal((oldValues) => {
        return {
          ...oldValues,
          ...newValues,
        };
      });

      if (!jobId) {
        console.error('Form values set without jobId');
        return;
      }

      console.log(`Saving form values: ${JSON.stringify(newValues)}`);
      saveFormValues({
        variables: {
          jobId,
          values: newValues,
        },
      });
    },
    [formValues, setFormValuesLocal, saveFormValues, jobId]
  );

  const setFormValue = useCallback<SetFormValue<FormDataType>>(
    (field: keyof FormDataType, value) => {
      if (!formValues) {
        return;
      }
      const oldValue = formValues[field];
      if (typeof value === 'function') {
        value = (value as any)(oldValue);
      }
      setFormValues({
        [field]: value,
      } as any);
    },
    [formValues, setFormValues, jobId]
  );

  const onFormInputChange = useCallback<EventHandler<any>>(
    (e) => {
      if (!e.target.name) {
        throw new Error(`${e.target.tagName} element does not have a name`);
      }
      setFormValue(e.target.name, e.target.value);
    },
    [setFormValue]
  );

  return {
    setFormValuesLocal,
    setJobId,
    formValues,
    setFormValue,
    setFormValues,
    onFormInputChange,
  };
};
